import locale from "@bonliva-crm/locale";

const e = locale.workflowErrors;

type Matcher = { match: (msg: string) => boolean; translate: (msg: string) => string };

function extractPensionPcts(msg: string): [string, string] {
  const form = msg.match(/form has ([\d.]+)%/)?.[1] ?? "?";
  const expected = msg.match(/indicates ([\d.]+)%/)?.[1] ?? "?";
  return [form, expected];
}

const matchers: Matcher[] = [
  // Salary transactions
  {
    match: (m) => m.includes("Salary transactions already exist"),
    translate: () => e.salaryTransactionsAlreadyExist
  },
  {
    match: (m) => m.includes("Failed to create salary transaction"),
    translate: () => e.failedToCreateSalaryTransaction
  },

  // Pension mismatch — sick pay checked first (more specific)
  {
    match: (m) => m.includes("Pension fee mismatch for sick pay form"),
    translate: (m) => e.pensionFeeMismatchSickPay(...extractPensionPcts(m))
  },
  {
    match: (m) => m.includes("Pension fee mismatch"),
    translate: (m) => e.pensionFeeMismatch(...extractPensionPcts(m))
  },

  // Employee number — sick pay checked first
  {
    match: (m) => m.includes("Employee number is missing for sick pay form"),
    translate: () => e.employeeNumberMissingSickPay
  },
  {
    match: (m) => m.includes("Employee number is missing"),
    translate: () => e.employeeNumberMissing
  },

  // Pay / sick pay form missing references — sick pay checked first
  {
    match: (m) => m.includes("has no consultantContractId") && m.includes("sick"),
    translate: () => e.sickPayFormMissingContractId
  },
  {
    match: (m) => m.includes("has no consultantContractId"),
    translate: () => e.payFormMissingContractId
  },
  {
    match: (m) => m.includes("Contract for sick pay form") && m.includes("no consultantId"),
    translate: () => e.sickPayContractMissingConsultantId
  },
  {
    match: (m) => m.includes("has no consultantId"),
    translate: () => e.payFormMissingConsultantId
  },

  // Empty records
  {
    match: (m) => m.includes("Empty sick records"),
    translate: () => e.emptySickRecords
  },
  {
    match: (m) => m.includes("Empty billing records"),
    translate: () => e.emptyBillingRecords
  },

  // Pay form format
  {
    match: (m) => m.includes("Invalid week/year on pay form"),
    translate: () => e.invalidWeekYearPayForm
  },

  // Consultant / email
  {
    match: (m) => m.includes("No email address available"),
    translate: () => e.consultantMissingEmail
  },
  {
    match: (m) => m.includes("Booking error: No consultant") || m.includes("No consultant"),
    translate: () => e.noConsultant
  },

  // Supplier / subcontractor
  {
    match: (m) => m.includes("No subcontractor form found"),
    translate: () => e.noSubcontractorForm
  },
  {
    match: (m) => m.includes("does not have a VAT number"),
    translate: () => e.supplierMissingVat
  },
  {
    match: (m) => m.includes("No active supplier found"),
    translate: () => e.noActiveSupplier
  },

  // Time report
  {
    match: (m) => m.includes("has no approved by customer date"),
    translate: () => e.timereportMissingApprovalDate
  },

  // Contracts — most specific first
  {
    match: (m) => m.includes("is already signed and cannot be cancelled"),
    translate: () => e.contractAlreadySigned
  },
  {
    match: (m) => m.includes("is not signed yet"),
    translate: () => e.contractNotSigned
  },
  {
    match: (m) => m.includes("does not have an external ID"),
    translate: () => e.contractMissingExternalId
  },
  {
    match: (m) => m.includes("Contract") && m.includes("not found"),
    translate: () => e.contractNotFound
  },
  {
    match: (m) =>
      m.includes("is not a self-invoicing contract") ||
      m.includes("is not a wcc-consultant-contract"),
    translate: () => e.contractWrongType
  }
];

/**
 * Translates a raw workflow error message to a user-friendly Swedish string from locale.
 * Returns a generic fallback if no pattern matches.
 */
export function translateWorkflowError(errorMessage: string | null): string | null {
  if (!errorMessage) return null;

  for (const { match, translate } of matchers) {
    if (match(errorMessage)) {
      return translate(errorMessage);
    }
  }

  return e.unknownError;
}
